import React, { useState, useRef, useEffect, useCallback } from 'react';

// Custom debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const SearchableSelect = ({
    value,
    onChange,
    options = [],
    placeholder = "Search and select...",
    displayKey = "name",
    valueKey = "id",
    className = "",
    disabled = false,
    required = false,
    loading = false,
    onSearch = null,
    searchPlaceholder = "Type to search...",
    minSearchLength = 0,
    debounceDelay = 300,
    error = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropStyle, setDropStyle] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [isSearching, setIsSearching] = useState(false);
    const [searchCache, setSearchCache] = useState(new Map());
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const searchInputRef = useRef(null);

    // Debounce search term for API calls
    const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);

    // Initialize filtered options
    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    // Handle search with debouncing and caching
    const performSearch = useCallback(async (term) => {
        if (!onSearch) return;

        // Check cache first
        if (searchCache.has(term)) {
            setFilteredOptions(searchCache.get(term));
            return;
        }

        try {
            setIsSearching(true);
            const result = await onSearch(term);

            // Cache the result
            setSearchCache(prev => new Map(prev).set(term, options));
            setFilteredOptions(options);
        } catch (error) {
            console.error('Search error:', error);
            setFilteredOptions([]);
        } finally {
            setIsSearching(false);
        }
    }, [onSearch, options, searchCache]);

    // Handle debounced search
    useEffect(() => {
        if (onSearch && debouncedSearchTerm.length >= minSearchLength) {
            performSearch(debouncedSearchTerm);
        } else if (!onSearch && searchTerm !== '') {
            // Local filtering for non-async dropdowns
            const filtered = options.filter(option =>
                option[displayKey]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredOptions(filtered);
        } else if (searchTerm === '') {
            // Reset to all options when search is cleared
            setFilteredOptions(options);
        }
    }, [debouncedSearchTerm, options, onSearch, displayKey, minSearchLength, performSearch, searchTerm]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle escape key to close dropdown
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    // Use position:fixed so the dropdown escapes ANY parent overflow/clip context
    // (modals, scrollable containers, flex overflow, etc.)
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const maxH = 260;
            const openUp = spaceBelow < maxH + 10;

            if (openUp) {
                setDropStyle({
                    position: 'fixed',
                    bottom: window.innerHeight - rect.top,
                    left: rect.left,
                    width: rect.width,
                    top: 'auto',
                    zIndex: 99999,
                });
            } else {
                setDropStyle({
                    position: 'fixed',
                    top: rect.bottom,
                    left: rect.left,
                    width: rect.width,
                    bottom: 'auto',
                    zIndex: 99999,
                });
            }
        }
    }, [isOpen]);

    const selectedOption = options.find(option => option[valueKey] === value);

    const handleSelect = (option) => {
        onChange(option[valueKey]);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleToggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setSearchTerm('');
            }
        }
    };

    const handleSearchChange = (e) => {
        e.stopPropagation();
        const newValue = e.target.value;
        setSearchTerm(newValue);
    };

    const handleSearchKeyDown = (e) => {
        e.stopPropagation();

        if (e.key === 'Enter') {
            e.preventDefault();
            // Select first option if available
            if (filteredOptions.length > 0) {
                handleSelect(filteredOptions[0]);
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            // Focus first option (could implement keyboard navigation here)
        }
    };

    const shouldShowLoading = loading || isSearching;
    const shouldShowNoResults = !shouldShowLoading && filteredOptions.length === 0;
    const hasMinSearchLength = onSearch && minSearchLength > 0;
    const searchTooShort = hasMinSearchLength && searchTerm.length > 0 && searchTerm.length < minSearchLength;

    return (
        <div className={`searchable-select ${className} ${error ? 'error' : ''}`} ref={dropdownRef}>
            <div
                ref={triggerRef}
                className={`searchable-select-input ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
                onClick={handleToggleDropdown}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleDropdown();
                    }
                }}
            >
                <span className={selectedOption ? 'selected' : 'placeholder'}>
                    {selectedOption ? selectedOption[displayKey] : placeholder}
                </span>
                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} dropdown-arrow`}></i>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown" style={dropStyle}>
                    <div className="search-input-container">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            placeholder={searchPlaceholder}
                            className="dropdown-search-input"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <i className="fas fa-search search-icon"></i>
                    </div>

                    <div className="options-container">
                        {shouldShowLoading ? (
                            <div className="loading-option">
                                <div className="spinner-sm"></div>
                                <span>Loading...</span>
                            </div>
                        ) : searchTooShort ? (
                            <div className="no-options">
                                Type at least {minSearchLength} characters to search
                            </div>
                        ) : shouldShowNoResults ? (
                            <div className="no-options">
                                {searchTerm ? 'No matching results found' : 'No options available'}
                            </div>
                        ) : (
                            filteredOptions.map(option => (
                                <div
                                    key={option[valueKey]}
                                    className={`option-item ${option[valueKey] === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                    onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                                >
                                    <span className="option-text">{option[displayKey]}</span>
                                    {option[valueKey] === value && (
                                        <i className="fas fa-check option-check"></i>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;